import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { computeScreenStatus, type ScreenStatus } from '../state/screen-status.js';

/**
 * Bütünsel ekran veri durumu bandı (Faz 1.18 K1/K3).
 *
 *   critical  -> kırmızı tam-ekran bandı + tüm hücreler grileşir (akış GERÇEKTEN kesik)
 *   awaiting  -> kırmızı bant: layout var ama hiç fiyat akmıyor (sessiz boş ekran önlenir)
 *   cache     -> sarı bant: Dexie önbelleğinden gösteriliyor, güncel doğrulanmadı
 *   calm      -> GİZLİ: bağlı + collector canlı ama fiyat sabit (sakin piyasa). Kırmızı
 *                alarm bastırılır + staleness-calm sınıfıyla hücreler normal kalır, ama
 *                müşteriye bakan ekranda bant gösterilmez (tazelik teknik kavram).
 *   ok        -> gizli
 *
 * Her 1sn durum yeniden hesaplanır. Emoji yok (CLAUDE.md UI kuralı) — inline SVG/CSS.
 */
@customElement('fp-staleness-banner')
export class FpStalenessBanner extends LitElement {
    @state() private status: ScreenStatus = { kind: 'ok', ageLabel: '' };

    private timer?: number;

    static styles = css`
        :host {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 9998;
        }
        .bar {
            padding: 0.75rem 1rem;
            text-align: center;
            font: 700 1.125rem/1.2 'Inter', system-ui, sans-serif;
            box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
        }
        .bar.critical,
        .bar.awaiting {
            background: #dc2626;
            color: #fff;
        }
        .bar.cache {
            background: #f59e0b;
            color: #000;
            font-weight: 600;
        }
        .ico {
            width: 1.15em;
            height: 1.15em;
            flex: 0 0 auto;
        }
        .hidden {
            display: none;
        }
    `;

    connectedCallback(): void {
        super.connectedCallback();
        this.timer = window.setInterval(() => this.refresh(), 1000);
        this.refresh();
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        if (this.timer !== undefined) clearInterval(this.timer);
        document.documentElement.classList.remove('staleness-critical', 'staleness-calm');
    }

    private refresh(): void {
        this.status = computeScreenStatus();
        // Yalnız 'critical'te hücreleri grileştir (gerçek alarm). 'calm' = sakin piyasa:
        // hücreler normal görünmeli (grileştirme/badge yok) → ayrı root sınıfı.
        document.documentElement.classList.toggle('staleness-critical', this.status.kind === 'critical');
        document.documentElement.classList.toggle('staleness-calm', this.status.kind === 'calm');
    }

    /** Uyarı üçgeni (inline SVG, emoji yok). */
    private warnIcon() {
        return html`<svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>`;
    }

    render() {
        const s = this.status;
        // 'calm' = sakin piyasa: durum KORUNUR (kırmızı alarmı bastırır + staleness-calm
        // sınıfıyla hücreleri normal tutar) ama müşteriye bakan ekranda BANT GÖSTERİLMEZ.
        // Tazelik teknik bir kavram; dış müşteri düşünmemeli, fiyatlar zaten geçerli (son
        // bilinen değer). Bant güven sarsabilir + uzun süre ekranda kalıp gürültü/burn-in
        // yaratırdı. Operatör tazelik bilgisini diagnostic mod / op panelinden görür.
        if (s.kind === 'ok' || s.kind === 'calm') {
            return html`<div class="hidden"></div>`;
        }
        let text: string;
        if (s.kind === 'critical') {
            text = `Fiyat akışı kesildi — son güncelleme ${s.ageLabel}`;
        } else if (s.kind === 'awaiting') {
            text = 'Fiyat verisi bekleniyor — bağlantı kontrol ediliyor';
        } else {
            text = 'Önbellekten gösteriliyor — güncel veriye bağlanılıyor';
        }
        return html`
            <div class="bar ${s.kind}">
                ${this.warnIcon()}
                <span>${text}</span>
            </div>
        `;
    }
}
